import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./index.module.css";
import ReportItemAllAttendees from "Reporting/Components/ReportItemAllAttendees";
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";
import { Select } from "shared/Select/Select";

const organizeRowsByBusiness = (rows = []) => {
  const byBusinessAndAttending = new Map();

  rows.forEach((row) => {
    const iboKey =
      row.IBO !== null && row.IBO !== undefined && row.IBO !== ""
        ? String(row.IBO)
        : `${row.iboALastName || ""}|${row.iboAFirstName || ""}|${
            row.iboBLastName || ""
          }|${row.iboBFirstName || ""}`;
    const attendingKey = String(row.attending || "")
      .trim()
      .toLowerCase();
    const rowKey = `${iboKey}::${attendingKey}`;

    if (!byBusinessAndAttending.has(rowKey)) {
      byBusinessAndAttending.set(rowKey, {
        ...row,
        columns: {},
        attendingTotal: 0,
      });
    }

    const current = byBusinessAndAttending.get(rowKey);
    const purchased = Number(row.purchased) || 0;
    const columnId = `${row.ticketGroup}::${row.ticket}`;

    current.columns[columnId] = (current.columns[columnId] || 0) + purchased;

    if (row.ticketGroup === "Attending Tickets") {
      current.attendingTotal += purchased;
    }

    const mergeIfEmpty = (field) => {
      if (!current[field] && row[field]) current[field] = row[field];
    };

    mergeIfEmpty("iboALastName");
    mergeIfEmpty("iboAFirstName");
    mergeIfEmpty("iboBLastName");
    mergeIfEmpty("iboBFirstName");
    mergeIfEmpty("pin");
    mergeIfEmpty("diamond");
    mergeIfEmpty("attending");
    mergeIfEmpty("email");
    mergeIfEmpty("phone");
    mergeIfEmpty("topicsId");
  });

  return Array.from(byBusinessAndAttending.values());
};

/*
 * Table displaying different items in a report with the functionality to sort and filter
 */
export default function ReportTableAllAttendees({
  reports,
  title,
  onSortedReportsChange,
}) {
  const PAGE_HEIGHT_PX = 425;
  const tableRef = useRef(null);
  const organizedReports = useMemo(
    () => organizeRowsByBusiness(Array.isArray(reports) ? reports : []),
    [reports]
  );

  const [pageBreakIndexes, setPageBreakIndexes] = useState([]);
  const [sort, setSort] = useState([]);

  // helper to collect all unique options for a given field
  const getAllOptions = (field) => {
    if (!Array.isArray(organizedReports)) return [];
    if (field === "pin") {
      return Array.from(
        new Set(organizedReports.map((r) => r.pin).filter(Boolean))
      );
    } else if (field === "diamond") {
      return Array.from(
        new Set(organizedReports.map((r) => r.diamond).filter(Boolean))
      );
    }
    return [];
  };

  const [selectedFilters, setSelectedFilters] = useState(() => {
    const initial = {};
    const pinOptions = getAllOptions("pin");
    if (pinOptions.length > 0) initial.pin = [...pinOptions];
    const diamondOptions = getAllOptions("diamond");
    if (diamondOptions.length > 0) initial.diamond = [...diamondOptions];
    return initial;
  });

  useEffect(() => {
    setSelectedFilters((prev) => {
      const next = { ...prev };

      const pinOptions = getAllOptions("pin");
      if (pinOptions.length > 0) {
        const prevPins = Array.isArray(prev.pin) ? prev.pin : [];
        const kept = prevPins.filter((p) => pinOptions.includes(p));
        const added = pinOptions.filter((p) => !prevPins.includes(p));
        next.pin = [...kept, ...added];
      } else {
        delete next.pin;
      }

      const diamondOptions = getAllOptions("diamond");
      if (diamondOptions.length > 0) {
        const prevDiamonds = Array.isArray(prev.diamond) ? prev.diamond : [];
        const keptD = prevDiamonds.filter((d) => diamondOptions.includes(d));
        const addedD = diamondOptions.filter((d) => !prevDiamonds.includes(d));
        next.diamond = [...keptD, ...addedD];
      } else {
        delete next.diamond;
      }

      return next;
    });
  }, [organizedReports]);

  const [sortedReports, setSortedReports] = useState(organizedReports || []);
  const [totalItem, setTotalItem] = useState({
    IBO: "Total",
    iboALastName: "",
    iboAFirstName: "",
    iboBLastName: "",
    iboBFirstName: "",
    pin: "",
    diamond: "",
    attending: "",
    email: "",
    phone: "",
    topicsId: "",
    columns: {},
  });

  const { allColumns, groupedHeaders } = useMemo(() => {
    // Step 1: group tickets by ticketGroup
    const byGroup = (Array.isArray(reports) ? reports : []).reduce((acc, r) => {
      if (!acc[r.ticketGroup]) acc[r.ticketGroup] = new Set();
      acc[r.ticketGroup].add(r.ticket);
      return acc;
    }, {});

    // Step 2: build grouped headers
    const grouped = Object.entries(byGroup)
      .sort(([a], [b]) => a.localeCompare(b)) // groups alphabetical
      .map(([ticketGroup, tickets]) => {
        // Sort tickets inside this group
        const sortedTickets = Array.from(tickets).sort((a, b) =>
          a.localeCompare(b)
        );

        // Build column defs for this group
        const cols = sortedTickets.map((ticketName) => ({
          id: `${ticketGroup}::${ticketName}`,
          name: ticketName,
          groupName: ticketGroup,
          headerName: ticketName,
          align: "center",
          getValue: (row) =>
            row.columns?.[`${ticketGroup}::${ticketName}`] || "",
        }));

        // Add "Total" column if this group is "Attending Tickets"
        if (ticketGroup === "Attending Tickets") {
          cols.push({
            id: `${ticketGroup}::Total`,
            name: "Total",
            groupName: ticketGroup,
            headerName: "Total",
            align: "center",
            getValue: (row) => row.attendingTotal || "",
          });
        }

        return {
          headerName: ticketGroup,
          columns: cols,
          colSpan: cols.length,
        };
      });

    // Step 3: flatten grouped columns into one list
    const allCols = grouped.flatMap((g) => g.columns);

    return { allColumns: allCols, groupedHeaders: grouped };
  }, [reports]);

  useEffect(() => {
    if (!sortedReports || sortedReports.length === 0) {
      setTotalItem(null);
      return;
    }

    const newTotal = {
      IBO: "Total",
      iboALastName: "",
      iboAFirstName: "",
      iboBLastName: "",
      iboBFirstName: "",
      pin: "",
      diamond: "",
      attending: "",
      email: "",
      phone: "",
      topicsId: "",
      columns: {},
    };

    allColumns.forEach((col) => {
      let total = 0;

      if (col.groupName === "Attending Tickets" && col.name === "Total") {
        total = sortedReports.reduce(
          (acc, row) => acc + (Number(row.attendingTotal) || 0),
          0
        );
      } else {
        total = sortedReports.reduce(
          (acc, row) => acc + (Number(row.columns?.[col.id]) || 0),
          0
        );
      }

      newTotal.columns[col.id] = total;
    });

    setTotalItem(newTotal);
  }, [sortedReports, allColumns]);

  useEffect(() => {
    let result = Array.isArray(organizedReports) ? [...organizedReports] : [];

    // If any selected filter is explicitly empty, return no results
    const hasEmptyFilter = Object.values(selectedFilters).some(
      (values) => Array.isArray(values) && values.length === 0
    );
    if (hasEmptyFilter) {
      setSortedReports([]);
      return;
    }

    // apply selectedFilters (pin, diamond)
    Object.entries(selectedFilters).forEach(([field, values]) => {
      if (!values || values.length === 0) return;
      result = result.filter((r) => {
        let compareVal;
        switch (field) {
          case "pin":
            compareVal = r.pin;
            break;
          case "diamond":
            compareVal = r.diamond;
            break;
          default:
            compareVal = "";
        }
        return values.includes(compareVal);
      });
    });

    // apply sorting (multi-sort)
    if (sort.length > 0) {
      result.sort((a, b) => {
        for (const s of sort) {
          if (!s.field || s.direction === "none") continue;
          let aVal, bVal;
          switch (s.field) {
            case "IBO":
              aVal = a.IBO;
              bVal = b.IBO;
              break;
            case "IBOA Last Name":
              aVal = a.iboALastName;
              bVal = b.iboALastName;
              break;
            case "IBOB Last Name":
              aVal = a.iboBLastName;
              bVal = b.iboBLastName;
              break;
            case "PIN":
              aVal = a.pin;
              bVal = b.pin;
              break;
            case "Diamond":
              aVal = a.diamond;
              bVal = b.diamond;
              break;
            default:
              aVal = 0;
              bVal = 0;
          }

          if (typeof aVal === "string") aVal = aVal.toLowerCase();
          if (typeof bVal === "string") bVal = bVal.toLowerCase();

          if (aVal < bVal) return s.direction === "asc" ? -1 : 1;
          if (aVal > bVal) return s.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    setSortedReports(result);
  }, [organizedReports, sort, selectedFilters]);

  const prevSortedReportsRef = useRef([]);

  useEffect(() => {
    const prev = prevSortedReportsRef.current;

    const changed =
      prev.length !== sortedReports.length ||
      JSON.stringify(prev) !== JSON.stringify(sortedReports);

    if (changed) {
      onSortedReportsChange?.(title, sortedReports);
      prevSortedReportsRef.current = sortedReports;
    }
  }, [sortedReports, title, onSortedReportsChange]);

  const updateFilter = (field, value) => {
    setSelectedFilters((prev) => {
      const current = prev[field] || [];
      let allOptions = getAllOptions(field);

      let newValues;
      if (value === "All") {
        // Toggle all: if all selected, clear; else select all
        if (current.length === allOptions.length) {
          newValues = [];
        } else {
          newValues = [...allOptions];
        }
      } else {
        // Toggle individual value
        if (current.includes(value)) {
          newValues = current.filter((v) => v !== value);
        } else {
          newValues = [...current, value];
        }
        // If after toggling, all are selected, treat as all selected
        if (newValues.length === allOptions.length) {
          newValues = [...allOptions];
        }
      }
      // Always remove any accidental 'All' string from the array
      newValues = newValues.filter((v) => v !== "All");
      return { ...prev, [field]: newValues };
    });
  };

  const toggleSort = (field) => {
    setSort((prev) => {
      const existingIndex = prev.findIndex((s) => s.field === field);
      let newSort;

      if (existingIndex === -1) {
        newSort = [...prev, { field, direction: "asc" }];
      } else {
        const existing = prev[existingIndex];
        let nextDirection;
        if (existing.direction === "asc") nextDirection = "desc";
        else if (existing.direction === "desc") nextDirection = "none";
        else nextDirection = "asc";

        if (nextDirection === "none") {
          newSort = prev.filter((_, i) => i !== existingIndex);
        } else {
          newSort = [...prev];
          newSort[existingIndex] = { field, direction: nextDirection };
        }
      }

      return newSort;
    });
  };

  const renderSortIcon = (field) => {
    const index = sort.findIndex((s) => s.field === field);
    if (index === -1 || sort[index].direction === "none") {
      return <FontAwesomeIcon icon={faMinus} className={styles.menuChevron} />;
    }

    const icon =
      sort[index].direction === "asc"
        ? faChevronUp
        : sort[index].direction === "desc"
        ? faChevronDown
        : faMinus;

    return (
      <div className={styles.sortWrapper}>
        <FontAwesomeIcon icon={icon} className={styles.menuChevron} />
        <span className={styles.sortPriority}>{index + 1}</span>
      </div>
    );
  };

  // Calculate page breaks for PDF/export, using a single ref
  const calculateBreaks = (ref, rowCount, setBreaks) => {
    if (!ref.current) {
      console.warn("Ref not available for break calculation");
      return;
    }

    const timeout = setTimeout(() => {
      const allElements = Array.from(
        ref.current.querySelectorAll(`.${styles.pageRowWrapper}`)
      );

      const newBreaks = [];
      // Get both the header and the title (headerInfo) heights
      const headerEl = ref.current.querySelector(`.${styles.header}`);
      const headerHeight = headerEl?.getBoundingClientRect().height || 0;
      // headerInfo is outside the tableRef, so get it from the parent
      let headerInfoHeight = 0;
      const parent = ref.current.parentElement;
      if (parent) {
        const headerInfoEl = parent.querySelector(`.${styles.headerInfo}`);
        if (headerInfoEl)
          headerInfoHeight = headerInfoEl.getBoundingClientRect().height;
      }

      let staticHeight = headerInfoHeight + headerHeight;
      // If headerInfoHeight is 0, manually add 50px for the title/header
      if (headerInfoHeight === 0) {
        staticHeight += 50;
        // console.log(
        //   "[PageBreak] headerInfoHeight missing, adding 50px manually"
        // );
      }
      // console.log(
      //   "[PageBreak] headerHeight:",
      //   headerHeight,
      //   "headerInfoHeight:",
      //   headerInfoHeight,
      //   "staticHeight:",
      //   staticHeight
      // );

      let heightSoFar = 0;
      let currentRowIndex = 0;
      let isFirstPage = true;

      allElements.forEach((el, i) => {
        const rect = el.getBoundingClientRect();
        // console.log(`[PageBreak] Row ${i} height:`, rect.height);
        if (isFirstPage) {
          heightSoFar += staticHeight;
          isFirstPage = false;
        }
        // Check if adding this row would exceed the page height
        if (heightSoFar + rect.height > PAGE_HEIGHT_PX) {
          newBreaks.push(currentRowIndex);
          // console.log(
          //   `[PageBreak] --- PAGE BREAK at row ${currentRowIndex}, resetting heightSoFar to row height:`,
          //   rect.height
          // );
          heightSoFar = rect.height;
        } else {
          heightSoFar += rect.height;
        }
        // console.log(`[PageBreak] After row ${i}, heightSoFar:`, heightSoFar);
        currentRowIndex++;
      });

      setBreaks(newBreaks);
    }, 100);

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    return calculateBreaks(
      tableRef,
      sortedReports.length + (totalItem ? 1 : 0),
      setPageBreakIndexes
    );
  }, [sortedReports, totalItem]);

  return (
    <div>
      {/* {index !== 0 && <div className={styles.pageBreak} />} */}
      <div className={styles.headerInfo}>
        <div className={styles.title}>
          <div className={styles.event}>{title}</div>
        </div>
      </div>

      <div className={styles.iboContainer}>
        <div className={styles.container} ref={tableRef}>
          <div className={styles.upperHeader}>
            <div
              className={styles.upperHeaderFiller}
              style={{ flex: `0 0 75%`, maxWidth: `75%` }}
            />
            {groupedHeaders.map((header) => {
              const headerColCount = header.columns.length;
              const totalColCount = allColumns.length;
              const widthPercent =
                totalColCount > 0 ? (headerColCount / totalColCount) * 25 : 0;

              return (
                <div
                  key={header.headerName}
                  className={styles.headerGroup}
                  style={{
                    flex: `0 0 ${widthPercent}%`,
                    maxWidth: `${widthPercent}%`,
                  }}
                >
                  <span className={styles.headerLabel}>
                    {header.headerName}
                  </span>
                </div>
              );
            })}
          </div>

          <div className={styles.header}>
            <div className={styles.ibo} onClick={() => toggleSort("IBO")}>
              IBO{renderSortIcon("IBO")}
            </div>
            <div
              className={styles.iboALastName}
              onClick={() => toggleSort("IBOA Last Name")}
            >
              IBOA Last Name{renderSortIcon("IBOA Last Name")}
            </div>
            <div className={styles.iboAFirstName}>IBOA First Name</div>
            <div
              className={styles.iboBLastName}
              onClick={() => toggleSort("IBOB Last Name")}
            >
              IBOB Last Name{renderSortIcon("IBOB Last Name")}
            </div>
            <div className={styles.iboBFirstName}>IBOB First Name</div>
            <div className={styles.pin}>
              <Select
                options={["All", ...getAllOptions("pin")].map((p) => p)}
                placeholder={
                  !selectedFilters.pin || selectedFilters.pin.length === 0
                    ? "PIN"
                    : selectedFilters.pin.length === getAllOptions("pin").length
                    ? "PIN"
                    : selectedFilters.pin.join(", ")
                }
                onChange={(value) => updateFilter("pin", value)}
                selectedValues={selectedFilters.pin || []}
              />
              <div onClick={() => toggleSort("PIN")}>
                {renderSortIcon("PIN")}
              </div>
            </div>
            <div className={styles.diamond}>
              <Select
                options={["All", ...getAllOptions("diamond")].map((d) => d)}
                placeholder={
                  !selectedFilters.diamond ||
                  selectedFilters.diamond.length === 0
                    ? "Diamond"
                    : selectedFilters.diamond.length ===
                      getAllOptions("diamond").length
                    ? "Diamond"
                    : selectedFilters.diamond.join(", ")
                }
                onChange={(value) => updateFilter("diamond", value)}
                selectedValues={selectedFilters.diamond || []}
              />
              <div onClick={() => toggleSort("Diamond")}>
                {renderSortIcon("Diamond")}
              </div>
            </div>
            <div className={styles.attending}>Attending</div>
            <div className={styles.email}>Email</div>
            <div className={styles.phone}>Phone</div>
            <div className={styles.topicsId}>Topics</div>
            {allColumns.map((col, index) => (
              <div
                key={col.id}
                className={styles.col}
                style={{
                  flex: `1 1 0`,
                  minWidth: 0,
                  width: `${25 / allColumns.length}%`,
                  textAlign: "center", // centers both header + values
                }}
              >
                {col.headerName}
              </div>
            ))}
          </div>

          {totalItem && (
            <React.Fragment key="total">
              {pageBreakIndexes.includes(sortedReports.length) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemAllAttendees
                  item={totalItem}
                  isOdd={sortedReports.length % 2 !== 0}
                  type="total"
                  allColumns={allColumns}
                  groupedHeaders={groupedHeaders}
                />
              </div>
            </React.Fragment>
          )}

          {sortedReports.map((row, i) => (
            <React.Fragment key={`${row.IBO}-${i}`}>
              {pageBreakIndexes.includes(i) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItemAllAttendees
                  item={row}
                  isOdd={i % 2 !== 0}
                  type="row"
                  allColumns={allColumns}
                  groupedHeaders={groupedHeaders}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

ReportTableAllAttendees.propTypes = {
  item: PropTypes.shape({
    eventOwner: PropTypes.string,
    eventType: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    events: PropTypes.arrayOf(PropTypes.object),
  }),
};
